// components/problems/problems-filter-popover.tsx
import {
  Check,
  CheckSquare,
  Filter,
  Gauge,
  List,
  RotateCcw,
  Search,
  Tags,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { FilterOperator, FilterValues } from '@/components/problems/types'
import { countActiveFilters } from '@/components/problems/types'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface ProblemsFilterPopoverProps {
  filters: FilterValues
  matchMode: 'any' | 'all'
  onMatchModeChange: (mode: 'any' | 'all') => void
  onToggleFilter: (
    field: 'status' | 'difficulty' | 'topics' | 'lists',
    value: string,
  ) => void
  onOperatorChange: (
    field: 'status' | 'difficulty' | 'topics' | 'lists',
    operator: FilterOperator,
  ) => void
  availableTopics: string[]
  availableDifficulties: string[]
  availableLists?: { id: string; name: string }[]
  onReset: () => void
}

export function ProblemsFilterPopover({
  filters,
  matchMode,
  onMatchModeChange,
  onToggleFilter,
  onOperatorChange,
  availableTopics,
  availableDifficulties,
  availableLists,
  onReset,
}: ProblemsFilterPopoverProps) {
  const [topicSearch, setTopicSearch] = useState('')
  const [open, setOpen] = useState(false)
  const tourHeld = useRef(false)
  const activeFilterCount = countActiveFilters(filters)
  const hasActiveFilters = activeFilterCount > 0

  useEffect(() => {
    const handleOpen = () => {
      tourHeld.current = true
      setOpen(true)
    }
    const handleClose = () => {
      tourHeld.current = false
      setOpen(false)
    }
    document.addEventListener('tour:open-filter', handleOpen)
    document.addEventListener('tour:close-filter', handleClose)
    return () => {
      document.removeEventListener('tour:open-filter', handleOpen)
      document.removeEventListener('tour:close-filter', handleClose)
    }
  }, [])

  const handleOpenChange = useCallback((next: boolean) => {
    if (!next && tourHeld.current) return
    setOpen(next)
  }, [])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors',
            hasActiveFilters
              ? 'bg-muted font-medium'
              : 'text-muted-foreground hover:text-foreground',
          )}
          style={hasActiveFilters ? { color: '#448EF7' } : undefined}
        >
          <Filter
            className="h-4 w-4"
            fill={hasActiveFilters ? 'currentColor' : 'none'}
          />
          {hasActiveFilters && <span>{activeFilterCount}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="problems-table-filter w-96 space-y-3"
      >
        <div className="flex items-center gap-2 text-sm">
          <span>Match</span>
          <select
            value={matchMode}
            onChange={(e) => onMatchModeChange(e.target.value as 'any' | 'all')}
            className="problems-table-select bg-background border-border rounded border px-2 py-1 text-sm"
          >
            <option value="any">Any</option>
            <option value="all">All</option>
          </select>
          <span>of the following:</span>
        </div>

        {/* Status */}
        <div className="flex items-center gap-2">
          <CheckSquare className="text-muted-foreground h-4 w-4 shrink-0" />
          <span className="w-16 shrink-0 text-sm font-medium">Status</span>
          <select
            value={filters.status.operator}
            onChange={(e) =>
              onOperatorChange('status', e.target.value as FilterOperator)
            }
            className="problems-table-select bg-background border-border w-16 shrink-0 rounded border px-2 py-1 text-sm"
          >
            <option value="is">is</option>
            <option value="is not">is not</option>
          </select>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="problems-table-select bg-background border-border flex-1 overflow-hidden rounded border px-2 py-1 text-left text-sm"
              >
                {filters.status.values.length === 0 ? (
                  <span className="text-muted-foreground">Any</span>
                ) : (
                  <span className="flex items-center gap-1">
                    {filters.status.values.map((v, i) => (
                      <span key={v} className="truncate">
                        {i > 0 && (
                          <span className="text-muted-foreground">, </span>
                        )}
                        {v === 'not started'
                          ? 'Not Started'
                          : v === 'attempted'
                            ? 'Attempted'
                            : 'Solved'}
                      </span>
                    ))}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="problems-table-filter w-44 p-1"
            >
              {(
                [
                  ['not started', 'Not Started', ''],
                  ['attempted', 'Attempted', 'text-yellow-500'],
                  ['solved', 'Solved', 'text-green-500'],
                ] as const
              ).map(([value, label, colorClass]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => onToggleFilter('status', value)}
                  className="hover:bg-muted/50 flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm"
                >
                  <span className={colorClass || undefined}>{label}</span>
                  {filters.status.values.includes(value) && (
                    <Check className="h-4 w-4 text-green-500" />
                  )}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </div>

        {/* Difficulty */}
        <div className="flex items-center gap-2">
          <Gauge className="text-muted-foreground h-4 w-4 shrink-0" />
          <span className="w-16 shrink-0 text-sm font-medium">Difficulty</span>
          <select
            value={filters.difficulty.operator}
            onChange={(e) =>
              onOperatorChange('difficulty', e.target.value as FilterOperator)
            }
            className="problems-table-select bg-background border-border w-16 shrink-0 rounded border px-2 py-1 text-sm"
          >
            <option value="is">is</option>
            <option value="is not">is not</option>
          </select>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="problems-table-select bg-background border-border flex-1 overflow-hidden rounded border px-2 py-1 text-left text-sm"
              >
                {filters.difficulty.values.length === 0 ? (
                  <span className="text-muted-foreground">Any</span>
                ) : (
                  <span className="flex items-center gap-1">
                    {filters.difficulty.values.map((d) => {
                      const label = d === 'Medium' ? 'Med.' : d
                      const colorClass =
                        d === 'Easy'
                          ? 'text-green-500'
                          : d === 'Medium'
                            ? 'text-yellow-500'
                            : 'text-red-500'
                      return (
                        <span key={d} className={colorClass}>
                          {label}
                        </span>
                      )
                    })}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="problems-table-filter w-44 p-1"
            >
              {availableDifficulties.map((d) => {
                const colorClass =
                  d === 'Easy'
                    ? 'text-green-500'
                    : d === 'Medium'
                      ? 'text-yellow-500'
                      : 'text-red-500'
                const label = d === 'Medium' ? 'Med.' : d
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => onToggleFilter('difficulty', d)}
                    className="hover:bg-muted/50 flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm"
                  >
                    <span className={colorClass}>{label}</span>
                    {filters.difficulty.values.includes(d) && (
                      <Check className="h-4 w-4 text-green-500" />
                    )}
                  </button>
                )
              })}
            </PopoverContent>
          </Popover>
        </div>

        {/* Topics */}
        <div className="flex items-center gap-2">
          <Tags className="text-muted-foreground h-4 w-4 shrink-0" />
          <span className="w-16 shrink-0 text-sm font-medium">Topics</span>
          <select
            value={filters.topics.operator}
            onChange={(e) =>
              onOperatorChange('topics', e.target.value as FilterOperator)
            }
            className="problems-table-select bg-background border-border w-16 shrink-0 rounded border px-2 py-1 text-sm"
          >
            <option value="is">is</option>
            <option value="is not">is not</option>
          </select>
          <Popover
            onOpenChange={(open) => {
              if (!open) setTopicSearch('')
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                className="problems-table-select bg-background border-border flex-1 truncate rounded border px-2 py-1 text-left text-sm"
              >
                {filters.topics.values.length === 0
                  ? 'Any'
                  : filters.topics.values.length === 1
                    ? filters.topics.values[0]
                    : `${filters.topics.values[0]} +${filters.topics.values.length - 1}`}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="problems-table-filter w-72 p-3"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <div className="relative mb-2">
                <Search className="text-muted-foreground absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="search"
                  value={topicSearch}
                  onChange={(e) => setTopicSearch(e.target.value)}
                  className="problems-table-search bg-background border-border w-full rounded border py-1.5 pl-7 pr-2 text-sm"
                />
              </div>
              <div className="flex max-h-48 flex-wrap gap-1.5 overflow-y-auto">
                {availableTopics
                  .filter((t) =>
                    t.toLowerCase().includes(topicSearch.toLowerCase()),
                  )
                  .map((topic) => {
                    const selected = filters.topics.values.includes(topic)
                    return (
                      <button
                        key={topic}
                        type="button"
                        onClick={() => onToggleFilter('topics', topic)}
                        className={cn(
                          'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
                          selected
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {topic}
                      </button>
                    )
                  })}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Lists */}
        {availableLists && availableLists.length > 0 && (
          <div className="flex items-center gap-2">
            <List className="text-muted-foreground h-4 w-4 shrink-0" />
            <span className="w-16 shrink-0 text-sm font-medium">Lists</span>
            <select
              value={filters.lists.operator}
              onChange={(e) =>
                onOperatorChange('lists', e.target.value as FilterOperator)
              }
              className="problems-table-select bg-background border-border w-16 shrink-0 rounded border px-2 py-1 text-sm"
            >
              <option value="is">is</option>
              <option value="is not">is not</option>
            </select>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="problems-table-select bg-background border-border flex-1 truncate rounded border px-2 py-1 text-left text-sm"
                >
                  {filters.lists.values.length === 0
                    ? 'Any'
                    : filters.lists.values.length === 1
                      ? filters.lists.values[0] === '__all__'
                        ? 'All Lists'
                        : (availableLists.find(
                            (l) => l.id === filters.lists.values[0],
                          )?.name ?? 'Any')
                      : `${filters.lists.values[0] === '__all__' ? 'All Lists' : (availableLists.find((l) => l.id === filters.lists.values[0])?.name ?? '')} +${filters.lists.values.length - 1}`}
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="problems-table-filter w-52 p-1"
              >
                <button
                  type="button"
                  onClick={() => onToggleFilter('lists', '__all__')}
                  className="hover:bg-muted/50 flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm font-medium"
                >
                  <span>All Lists</span>
                  {filters.lists.values.includes('__all__') && (
                    <Check className="h-4 w-4 text-green-500" />
                  )}
                </button>
                <div className="bg-border my-1 h-px" />
                {availableLists.map((list) => (
                  <button
                    key={list.id}
                    type="button"
                    onClick={() => onToggleFilter('lists', list.id)}
                    className="hover:bg-muted/50 flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm"
                  >
                    <span>{list.name}</span>
                    {filters.lists.values.includes(list.id) && (
                      <Check className="h-4 w-4 text-green-500" />
                    )}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          </div>
        )}

        {hasActiveFilters && (
          <button
            type="button"
            onClick={onReset}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
        )}
      </PopoverContent>
    </Popover>
  )
}
